import { Box, Paper, Typography, Theme, Tooltip } from "@mui/material";
import { Warning as WarningIcon } from "@mui/icons-material";
import { useTheme } from "@mui/material/styles";
import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import type { EquipmentTreeRowFlat } from "../../models";
import {
  usePartDetailsQuery,
  type EqpSection,
  type GetPartDetailsFn,
} from "../../hooks/queries";
import type { PartDetailsParams } from "../../utils/getPartDetailsParams";

interface PartDetailsMetrics {
  MetricTypeId: number;
  MetricTypeName: string;
  CampaignValue: number | null;
  CampaignValueStatus: string;
  CampaignWarningLimit: number | null;
  CampaignAlertLimit: number | null;
  PartialValue: number | null;
  PartialValueStatus: string;
  PartialWarningLimit: number | null;
  PartialAlertLimit: number | null;
  TotalValue: number | null;
  TotalValueStatus: string;
  TotalWarningLimit: number | null;
  TotalAlertLimit: number | null;
}

interface MasterData {
  InstalledOn: Date | null;
  ResetOn: Date | null;
  UninstalledOn?: Date | null;
}

// =============================================================================
// Props
// =============================================================================

interface Props {
  section: EqpSection;
  partDetailsParams: PartDetailsParams | null;
  queryFn: GetPartDetailsFn;
  selectedRow: EquipmentTreeRowFlat | null;
  /** Uninstallation date (only for offline equipment) */
  uninstalledOn?: Date | null;
  /** Installation date of the currently online equipment (for stale end-campaign detection) */
  onlineEquipmentInstalledOn?: Date | null;
  isSpareContainer: (containerId: number | undefined) => boolean;
}

// =============================================================================
// Helper Functions (module-level, stable references)
// =============================================================================

/**
 * Get background color based on status code
 * O = OK (default), W = Warning (yellow), A = Alert (red), B = Blank (gray)
 */
const getStatusColor = (status: string, theme: Theme): string | undefined => {
  switch (status) {
    case "A":
      return theme.palette.error.main;
    case "W":
      return theme.palette.warning.main;
    default:
      return undefined;
  }
};

/** Format a number for display (with thousands separator) */
const formatNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "-";
  return value.toLocaleString();
};

/** Format limits as "Warning / Alert" or just show available values */
const formatLimits = (
  warning: number | null | undefined,
  alert: number | null | undefined,
  theme: Theme,
): React.ReactNode => {
  if (
    (warning === null || warning === undefined) &&
    (alert === null || alert === undefined)
  ) {
    return "-";
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
      {warning !== null && warning !== undefined ? (
        <Typography
          component="span"
          sx={{
            fontSize: "0.75rem",
            color: theme.palette.warning.main,
            fontWeight: 500,
          }}
        >
          {formatNumber(warning)}
        </Typography>
      ) : null}
      {alert !== null && alert !== undefined ? (
        <Typography
          component="span"
          sx={{
            fontSize: "0.75rem",
            color: theme.palette.error.main,
            fontWeight: 500,
          }}
        >
          {formatNumber(alert)}
        </Typography>
      ) : null}
    </Box>
  );
};

/** Format date for display in header (short format) */
const formatDate = (date: Date | null | undefined): string | null => {
  if (!date) return null;
  return dxtToLocalServerTime(date, "MM/dd/yyyy");
};

// =============================================================================
// Component
// =============================================================================

export const MetricsPanel = ({
  section,
  partDetailsParams,
  queryFn,
  selectedRow,
  uninstalledOn,
  onlineEquipmentInstalledOn,
  isSpareContainer,
}: Props) => {
  const theme = useTheme();

  const queryResult = usePartDetailsQuery(section, partDetailsParams, queryFn);

  const metrics = (queryResult.data?.tables[1]?.rows ??
    []) as unknown as PartDetailsMetrics[];
  const isLoading = queryResult.isLoading;
  const masterRow = queryResult.data?.tables[0]?.rows[0] as unknown as
    | MasterData
    | undefined;
  const installedOn = masterRow?.InstalledOn ?? null;
  const resetOn = masterRow?.ResetOn ?? null;

  // Detect stale end-campaign: offline equipment's end date is after the online equipment's start date.
  // This means the user backdated the online equipment's campaign but didn't update the offline one.
  const isEndCampaignStale =
    uninstalledOn &&
    onlineEquipmentInstalledOn &&
    new Date(uninstalledOn).getTime() >
      new Date(onlineEquipmentInstalledOn).getTime();

  const isSpareContainerSelected =
    selectedRow?.rowType === "sparesContainer" &&
    isSpareContainer(selectedRow?.ContainerId);

  const isLocationSelected = selectedRow && selectedRow.rowType !== "equipment";
  const hasPart = selectedRow?.HasAssignedPart ?? false;

  // Determine if we should show partial column (only for panels, not roofs/sidewalls)
  const showPartial = selectedRow && selectedRow.rowType !== "equipment";

  const getTitle = () => {
    if (!selectedRow) return "Metrics";
    if (
      selectedRow.rowType === "container" ||
      selectedRow.rowType === "sparesContainer"
    ) {
      return `Metrics - ${selectedRow.ContainerName ?? ""}`;
    }
    if (selectedRow.rowType === "sparePart") {
      return `Metrics - ${selectedRow.L2PartName ?? ""}`;
    }
    if (selectedRow.rowType === "equipment") {
      return `Metrics - ${selectedRow.EquipmentName}`;
    }
    return `Metrics - ${selectedRow.EquipmentName ?? ""} / ${selectedRow.PositionName ?? ""}`;
  };

  // Common cell styles
  const cellStyle = {
    padding: theme.spacing(0.75, 1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    fontSize: "0.8rem",
  };

  const headerCellStyle = {
    ...cellStyle,
    fontWeight: 600,
    backgroundColor: theme.palette.grey[100],
    textAlign: "center" as const,
  };

  const subHeaderCellStyle = {
    ...cellStyle,
    fontWeight: 500,
    backgroundColor: theme.palette.grey[50],
    textAlign: "center" as const,
    fontSize: "0.75rem",
  };

  const valueCellStyle = {
    ...cellStyle,
    textAlign: "center" as const,
  };

  return (
    <Paper elevation={2}>
      <Box sx={{ p: 1.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          {getTitle()}
        </Typography>
        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: "4px",
            overflow: "hidden",
          }}
        >
          {isSpareContainerSelected ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 2 }}
            >
              Please Select a Part
            </Typography>
          ) : !selectedRow ||
            (selectedRow.rowType !== "equipment" &&
              selectedRow.rowType !== "location" &&
              selectedRow.rowType !== "sparePart") ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 2 }}
            >
              Select a part to view metrics
            </Typography>
          ) : isLocationSelected && !hasPart ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 2 }}
            >
              No part installed
            </Typography>
          ) : isLoading ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 2 }}
            >
              Loading...
            </Typography>
          ) : metrics.length === 0 ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center", py: 2 }}
            >
              No metrics available
            </Typography>
          ) : (
            <Box
              component="table"
              sx={{ width: "100%", borderCollapse: "collapse" }}
            >
              <Box component="thead">
                {/* Main header row */}
                <Box component="tr">
                  <Box
                    component="th"
                    rowSpan={2}
                    sx={{ ...headerCellStyle, width: "80px" }}
                  >
                    Metric
                  </Box>
                  <Box component="th" colSpan={2} sx={headerCellStyle}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <span>Campaign</span>
                      {formatDate(installedOn) ? (
                        <Typography
                          component="span"
                          sx={{
                            fontSize: "0.65rem",
                            fontWeight: 400,
                            color: "text.secondary",
                            whiteSpace: "nowrap",
                          }}
                        >
                          from {formatDate(installedOn)}
                          {uninstalledOn ? (
                            isEndCampaignStale ? (
                              <Tooltip
                                title="End date is after the online equipment's start campaign — update it to match"
                                arrow
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    color: "error.main",
                                    fontWeight: 500,
                                  }}
                                >
                                  {" "}
                                  to {formatDate(uninstalledOn)}{" "}
                                  <WarningIcon
                                    sx={{
                                      fontSize: "0.75rem",
                                      verticalAlign: "middle",
                                    }}
                                  />
                                </Box>
                              </Tooltip>
                            ) : (
                              ` to ${formatDate(uninstalledOn)}`
                            )
                          ) : null}
                        </Typography>
                      ) : null}
                    </Box>
                  </Box>
                  {showPartial ? (
                    <Box component="th" colSpan={2} sx={headerCellStyle}>
                      <Box sx={{ display: "flex", flexDirection: "column" }}>
                        <span>Partial</span>
                        {formatDate(resetOn) ? (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: "0.65rem",
                              fontWeight: 400,
                              color: "text.secondary",
                            }}
                          >
                            from {formatDate(resetOn)}
                          </Typography>
                        ) : null}
                      </Box>
                    </Box>
                  ) : null}
                  <Box component="th" colSpan={2} sx={headerCellStyle}>
                    Total
                  </Box>
                </Box>
                {/* Sub-header row */}
                <Box component="tr">
                  <Box component="th" sx={subHeaderCellStyle}>
                    Heats
                  </Box>
                  <Box component="th" sx={subHeaderCellStyle}>
                    Limits
                  </Box>
                  {showPartial ? (
                    <>
                      <Box component="th" sx={subHeaderCellStyle}>
                        Heats
                      </Box>
                      <Box component="th" sx={subHeaderCellStyle}>
                        Limits
                      </Box>
                    </>
                  ) : null}
                  <Box component="th" sx={subHeaderCellStyle}>
                    Heats
                  </Box>
                  <Box component="th" sx={subHeaderCellStyle}>
                    Limits
                  </Box>
                </Box>
              </Box>
              <Box component="tbody">
                {metrics.map((metric) => (
                  <Box component="tr" key={metric.MetricTypeId}>
                    {/* Metric Name */}
                    <Box
                      component="td"
                      sx={{
                        ...valueCellStyle,
                        fontWeight: 500,
                        textAlign: "left",
                      }}
                    >
                      {metric.MetricTypeName}
                    </Box>

                    {/* Campaign Current */}
                    <Box
                      component="td"
                      sx={{
                        ...valueCellStyle,
                        backgroundColor: getStatusColor(
                          metric.CampaignValueStatus,
                          theme,
                        ),
                        color:
                          metric.CampaignValueStatus === "A" ||
                          metric.CampaignValueStatus === "W"
                            ? theme.palette.common.white
                            : undefined,
                        fontWeight:
                          metric.CampaignValueStatus !== "O" ? 600 : undefined,
                      }}
                    >
                      {formatNumber(metric.CampaignValue)}
                    </Box>

                    {/* Campaign Limits */}
                    <Box component="td" sx={valueCellStyle}>
                      {formatLimits(
                        metric.CampaignWarningLimit,
                        metric.CampaignAlertLimit,
                        theme,
                      )}
                    </Box>

                    {/* Partial Current */}
                    {showPartial ? (
                      <>
                        <Box
                          component="td"
                          sx={{
                            ...valueCellStyle,
                            backgroundColor: getStatusColor(
                              metric.PartialValueStatus,
                              theme,
                            ),
                            color:
                              metric.PartialValueStatus === "A" ||
                              metric.PartialValueStatus === "W"
                                ? theme.palette.common.white
                                : undefined,
                            fontWeight:
                              metric.PartialValueStatus !== "O"
                                ? 600
                                : undefined,
                          }}
                        >
                          {formatNumber(metric.PartialValue)}
                        </Box>

                        {/* Partial Limits */}
                        <Box component="td" sx={valueCellStyle}>
                          {formatLimits(
                            metric.PartialWarningLimit,
                            metric.PartialAlertLimit,
                            theme,
                          )}
                        </Box>
                      </>
                    ) : null}

                    {/* Total Current */}
                    <Box
                      component="td"
                      sx={{
                        ...valueCellStyle,
                        backgroundColor: getStatusColor(
                          metric.TotalValueStatus,
                          theme,
                        ),
                        color:
                          metric.TotalValueStatus === "A" ||
                          metric.TotalValueStatus === "W"
                            ? theme.palette.common.white
                            : undefined,
                        fontWeight:
                          metric.TotalValueStatus !== "O" ? 600 : undefined,
                      }}
                    >
                      {formatNumber(metric.TotalValue)}
                    </Box>

                    {/* Total Limits */}
                    <Box component="td" sx={valueCellStyle}>
                      {formatLimits(
                        metric.TotalWarningLimit,
                        metric.TotalAlertLimit,
                        theme,
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );
};
